import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { AssetContainer } from '~/techstyle-shared/react-assets';
import {
  Button,
  desktop,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import CompressionGuideCarousel from '../CompressionGuideCarousel';
import CompressionOverlayStickyHeader from '../CompressionOverlayStickyHeader';

const AssetWrapper = styled.div`
  min-height: 281px;
  text-align: center;
`;

const ButtonContainer = styled.div`
  padding: 24px 48px 8px;
  text-align: center;

  ${desktop`
    padding-bottom: 48px;
    ${({ $dark }) =>
      $dark &&
      css`
        background: ${({ theme }) =>
          theme.colors.compressionGuideOverlayButtonBg};
      `}
  `}
`;

const QuoteSection = styled.div`
  padding: 48px 32px;
  text-align: center;
`;

const IconsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: center;
  margin-top: ${({ theme }) => theme.sizes.sm}px;

  ${desktop`
    margin-top: ${({ theme }) => theme.sizes.lg}px;
    padding: 0 18%;
  `}
`;

const Info = styled.p`
  font-weight: 400;
  font-size: 12px;
  line-height: ${({ theme }) => theme.sizes.sm}px;
  letter-spacing: 0.2px;
  color: ${({ theme }) => theme.colors.compressionGuideInfo};
  margin-top: ${({ theme }) => theme.sizes.xxs}px;

  ${desktop`
    padding: 0 18%;
    text-align: center;
  `}
`;

const Overlay = styled.div`
  margin: 0 auto;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.palette.white};
  max-width: 848px;
  margin-top: 170px;
  margin-bottom: 170px;
`;

const Section = styled.div`
  padding: ${({ theme }) => theme.sizes.lg}px;

  ${desktop`
    padding: ${({ theme }) => theme.sizes.xxl}px;

    ${({ $dark }) =>
      $dark &&
      css`
        background: ${({ theme }) =>
          theme.colors.compressionGuideOverlaySectionBg};
      `}
  `}
`;

const Carousel = styled.div`
  ${desktop`
    background: ${({ theme }) =>
      theme.colors.compressionGuideOverlayCarouselBg};
  `}
`;

const WhyWeLoveItSection = styled(Section)`
  padding-bottom: ${({ theme }) => theme.sizes.sm}px;

  ${desktop`
    padding-bottom: ${({ theme }) => theme.sizes.lg}px;
  `}
`;

const SectionHeader = styled.h4`
  font-weight: bold;
  font-size: 22px;
  line-height: 26px;
  max-width: 65%;
  margin-bottom: ${({ theme }) => theme.sizes.xxs}px;

  ${desktop`
    text-align: center;
    max-width: 100%;
  `}
`;

const StyledLink = styled(Button)`
  ${desktop`
    width: auto;
    padding: 16px 90px;
    margin-top: 16px;
  `}
`;

const IconWrapper = styled.div`
  text-align: center;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  flex-basis: 25%;

  ${desktop`
    flex-basis: 20%;
  `}

  & > div {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: ${({ theme }) => theme.sizes.md}px;
  }

  picture {
    margin-bottom: ${({ theme }) => theme.sizes.xs}px;
  }
`;

const StyledAssetContainer = styled(AssetContainer)`
  width: 100%;
`;

const CompressionGuideOverlay = ({ children, content, close }) => {
  const { isMobile } = useBreakpoint();

  return (
    // full-width overlay causes background close not to work, so add it back here
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div onClick={close} tabIndex={-1}>
      <Overlay onClick={(e) => e.stopPropagation()}>
        <CompressionOverlayStickyHeader content={content} close={close} />
        <AssetContainer
          name={isMobile ? content.quote.mobile : content.quote.desktop}
          aspectRatio="auto"
        >
          {({ data }) =>
            // hide entire section if asset is not returned
            !data ? null : (
              <QuoteSection>
                <AssetContainer.ImageAsset asset={data} />
              </QuoteSection>
            )
          }
        </AssetContainer>
        <div>
          <WhyWeLoveItSection $dark>
            <SectionHeader>
              <FormattedMessage
                defaultMessage="Why We Love It"
                id="compression_guide.love_it_header"
              />
            </SectionHeader>
            <Info>{content.loveItParagraph.message}</Info>
          </WhyWeLoveItSection>
          <Carousel>
            <CompressionGuideCarousel images={content.carousel} />
          </Carousel>
          <ButtonContainer $dark>
            <StyledLink variant="primary" href={content.shop.link}>
              {content.shop.message}
            </StyledLink>
          </ButtonContainer>
        </div>
        <Section>
          <SectionHeader>{content.difference.message}</SectionHeader>
          <Info>{content.differenceParagraph.message}</Info>
          <IconsContainer>
            {content.icons.map((row) => (
              <AssetContainer
                name={row.asset}
                aspectRatio="auto"
                key={row.asset}
              >
                {({ data }) =>
                  // hide entire section if asset is not returned
                  !data ? null : (
                    <IconWrapper>
                      <div>
                        <AssetContainer.ImageAsset asset={data} />
                        {row.message}
                      </div>
                    </IconWrapper>
                  )
                }
              </AssetContainer>
            ))}
          </IconsContainer>
        </Section>
        <AssetWrapper>
          <StyledAssetContainer
            aspectRatio="auto"
            name={
              isMobile
                ? content.imageSection.mobile
                : content.imageSection.desktop
            }
          />
        </AssetWrapper>
        <ButtonContainer>
          <StyledLink variant="primary" href={content.shop.link}>
            {content.shop.message}
          </StyledLink>
        </ButtonContainer>
        {children}
      </Overlay>
    </div>
  );
};

const TranslationType = PropTypes.shape({
  message: PropTypes.node.isRequired,
});

CompressionGuideOverlay.propTypes = {
  children: PropTypes.node,
  close: PropTypes.func,
  content: PropTypes.shape({
    icons: PropTypes.arrayOf(
      PropTypes.shape({
        asset: PropTypes.string.isRequired,
        message: PropTypes.node.isRequired,
      })
    ).isRequired,
    loveItParagraph: TranslationType.isRequired,
    differenceParagraph: TranslationType.isRequired,
    difference: TranslationType.isRequired,
    shop: PropTypes.shape({
      message: PropTypes.node.isRequired,
      link: PropTypes.string.isRequired,
    }).isRequired,
    imageSection: PropTypes.shape({
      mobile: PropTypes.string.isRequired,
      desktop: PropTypes.string.isRequired,
    }).isRequired,
    quote: PropTypes.shape({
      mobile: PropTypes.string.isRequired,
      desktop: PropTypes.string.isRequired,
    }).isRequired,
    carousel: PropTypes.shape({
      mobile: PropTypes.arrayOf(PropTypes.string).isRequired,
      desktop: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
  }),
};

export default CompressionGuideOverlay;
